import type { FieldTree } from '@angular/forms/signals';

import type { ExtendedPositionLegacy, WidgetPositionLegacy } from '@sites/entity-core';

/**
 * Minimal `data` shape for {@link widgetPositionPlugin} — the
 * **default-placement** flavour. Schema twin in entity-core:
 * `widgetPositionPluginShapeLegacy` (`widget/common/schemas/legacy/widget-position.ts`)
 * — `nullish()`: widgets persisted before the position group shipped omit
 * the key entirely.
 */
export type WidgetPositionPluginData = {
    position?: WidgetPositionLegacy | null;
};

export type WidgetPositionApi = {
    /** `undefined` while the key is absent from the persisted data (nullish schema). */
    readonly position: FieldTree<WidgetPositionLegacy | null | undefined> | undefined;
    setPosition: (value: WidgetPositionLegacy | null | undefined) => void;
};

/**
 * Minimal `data` shape for {@link extendedPositionPlugin} — the
 * **zero-block-placement** flavour. Schema twin in entity-core:
 * `extendedPositionPluginShapeLegacy` (`widget/common/schemas/legacy/extended-position.ts`),
 * required by every zero data schema.
 */
export type ExtendedPositionPluginData = {
    position: ExtendedPositionLegacy;
};

export type ExtendedPositionApi = {
    readonly position: FieldTree<ExtendedPositionLegacy>;
    setPosition: (value: ExtendedPositionLegacy) => void;
};

/**
 * Simple per-device position grid (default-placement widgets) as a FieldTree
 * accessor + setter sugar. The two position plugins expose the same API keys
 * on purpose — each variant assembler installs the flavour matching its
 * schema, and union-typed consumers narrow per variant.
 *
 * Receives the form's `data` FieldTree **lazily** (`() => _raw.data`) and
 * exposes getters, so tree paths only materialise on consumer access —
 * compose with {@link import('../../core/compose-form').composeForm}, never
 * object spread. Note the `position` key may be absent on persisted data
 * (nullish schema) — then the FieldTree child never materialises and the
 * accessor returns `undefined` at runtime; the setter therefore writes
 * **through the parent node**, which works whether or not the key exists.
 */
export function widgetPositionPlugin<D extends WidgetPositionPluginData>(data: () => FieldTree<D>): WidgetPositionApi {
    // FieldTree is invariant (writable signals), so the narrowing is done via
    // an explicit cast — safe by the `D extends WidgetPositionPluginData` constraint.
    const d = data as unknown as () => FieldTree<WidgetPositionPluginData>;
    return {
        get position() {
            return d().position;
        },
        setPosition: (value: WidgetPositionLegacy | null | undefined) => {
            d()().value.update((parent) => ({ ...parent, position: value }));
        },
    };
}

/**
 * Full extended-position grid (zero-block widgets: per-device
 * width/height/x/y/rotate/zIndex plus anchor sides) as a single FieldTree
 * branch + setter sugar. Consumers reach per-axis fields via
 * `form.position().value().width` or by walking the FieldTree
 * (`form.position.width()`) — there is intentionally no per-axis accessor
 * surface (22 fields). See {@link widgetPositionPlugin} for the composition
 * and laziness contract.
 */
export function extendedPositionPlugin<D extends ExtendedPositionPluginData>(
    data: () => FieldTree<D>
): ExtendedPositionApi {
    // FieldTree is invariant (writable signals), so the narrowing is done via
    // an explicit cast — safe by the `D extends ExtendedPositionPluginData` constraint.
    const d = data as unknown as () => FieldTree<ExtendedPositionPluginData>;
    return {
        get position() {
            return d().position;
        },
        setPosition: (value: ExtendedPositionLegacy) => {
            d().position().value.set(value);
        },
    };
}
